import { priorInformationCallOffered } from "../../../../../common/procurement/priorInformationCall";
import {
  Box,
  FormControl,
  FormLabel,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { offersAcceleratedProcedure, usesAcceleratedProcedure } from "../../../../../common/procurement/karens";
import { AcceleratedProcedureField } from "../components/AcceleratedProcedureField";
import {
  procedureAvailability,
  procedureLegalBasis,
  type ProcedureInputs,
} from "../../../../../common/procurement/procedureAvailability";
import { DesignContestField } from "../components/DesignContestField";
import { isDesignContest } from "../utils/designContest";
import { useTranslation } from "react-i18next";
import { StepNavigation } from "../../../../../common/StepNavigation";
import {
  ProcedureType,
  Regulation,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import {
  procedureShownAs,
  requiresProcedureJustification,
  type ProcedureTypeOption,
} from "../../../../../common/procurement/publicProcurementOptions";
import {
  allowsNegotiation,
  isDpsCompetition,
  isDpsScheme,
  isLeftToTheBuyer,
  isQualificationScheme,
  isTwoStageProcedure,
  offersAwardOnInitialTenders,
  offersStagedReduction,
} from "../utils/procedure";
import {
  isFoaNationalTier,
  minCandidatesWhenCapped,
} from "../utils/karens";
import { CandidateLimitsField } from "../components/CandidateLimitsField";
import { useReadinessByField } from "../hooks/useReadinessByField";
import { DpsSchemeFields } from "../components/DpsSchemeFields";
import { QualificationSchemeFields } from "../components/QualificationSchemeFields";
import { PriorInformationCallField } from "../components/PriorInformationCallField";
import { NegotiationTermsField } from "../components/NegotiationTermsField";
import { StagedReductionField } from "../components/StagedReductionField";
import { DialoguePlanField } from "../components/DialoguePlanField";
import { isFoaPartII } from "../utils/foaPartII";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { SubmissionLanguagesField } from "../components/SubmissionLanguagesField";
import { isAnnouncedPartI } from "../../../../../common/procurement/foaPartDisplay";
import { MilestonePlanCard } from "../components/MilestonePlanCard";
import { PlaceOfPerformanceField } from "../components/PlaceOfPerformanceField";
import { NoCompetitionJustificationField } from "../components/NoCompetitionJustificationField";
import { NegotiationWithoutNoticeField } from "../components/NegotiationWithoutNoticeField";
import { isNegotiationWithoutNotice } from "../utils/negotiationWithoutNotice";
import {
  followsDesignContestBelowEeaTier,
  isWithoutCompetition,
} from "../utils/noCompetition";
import { QualificationSchemeInvitees } from "../components/qualificationScheme/QualificationSchemeInvitees";
import { isUnavailableInItsPart, ProcedureUnavailableNotice } from "../components/ProcedureUnavailableNotice";
import { ProcedureShelves, type ProcedureEntry } from "../components/ProcedureShelves";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const ProcedureStep = ({
  eventId,
  previousStep,
  nextStep,
  nextStepLabel,
}: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<
    SourcingEventDto,
    SourcingEventPatchDto
  >(eventId);
  const { refusedProcedure } = useReadinessByField(eventId);
  const publicContext =
    state.eventContext.type === "Public" ? state.eventContext : null;
  const savedProcedureRefused = refusedProcedure !== null && refusedProcedure === publicContext?.procedureType;

  const isSaving = (field: string) => {
    const s = fieldStatus(field);
    return s === "saving" || s === "pending";
  };

  const procedureType = publicContext?.procedureType ?? "";
  const regulation = publicContext?.regulation ?? null;
  const procedureInputs: ProcedureInputs = {
    regulation,
    foaPart: publicContext?.foaPart,
    contractNature: publicContext?.contractNature,
    serviceCategory: publicContext?.serviceCategory,
    estimatedValueNok: publicContext?.estimatedValue?.value,
    announcedOnDoffin: publicContext?.announcedOnDoffin,
  };
  const {
    options: procedureOptions,
    partIHealthSocialEducation,
    overEeaThreshold,
    partIV,
    underUtilities,
    offersTwoStage,
    meetsPartRequirement,
    offersWithoutCompetition: tierOffersWithoutCompetition,
    offersDesignContest: tierOffersDesignContest,
  } = procedureAvailability(procedureInputs);
  const selectedProcedure = procedureShownAs(
    publicContext?.procedureType,
    regulation,
    publicContext?.foaPart,
  );
  const optionBarredByTier = (option: ProcedureTypeOption) =>
    !meetsPartRequirement(option.value);
  const optionComingSoon = (option: ProcedureTypeOption) =>
    !option.enabled && !optionBarredByTier(option);
  const eeaTierLabel =
    regulation === Regulation.Forsyningsforskriften
      ? t("Requires part II or above (over the EEA threshold)")
      : t("Requires FOA part III (above the EEA threshold)");
  const twoStageBarredLabel = partIV
    ? t(
        "Not available in FOA part IV — choose the open procedure or a tender competition",
      )
    : eeaTierLabel;
  const dpsBarredLabel = isFoaPartII(regulation, publicContext?.foaPart)
    ? t(
        "Not available in FOA part II — choose open or restricted tender competition",
      )
    : twoStageBarredLabel;

  const withoutCompetition = isWithoutCompetition(publicContext?.procedureType);
  const showsWithoutCompetition =
    withoutCompetition || tierOffersWithoutCompetition;
  const withoutNotice = isNegotiationWithoutNotice(
    publicContext?.procedureType,
  );
  const showsWithoutNotice = withoutNotice || tierOffersWithoutCompetition;
  const designContest = isDesignContest(publicContext?.procedureType);
  const offersContest = designContest || tierOffersDesignContest;
  const contestBarred = !offersContest && !overEeaThreshold;
  const showsDesignContest = offersContest || contestBarred;
  const contestBarredLabel = underUtilities
    ? eeaTierLabel
    : t("zz_FieldErrorCode_DesignContestBelowPartV");

  const underQualificationScheme =
    publicContext?.qualificationSchemeCompetition;
  if (underQualificationScheme && publicContext?.procedureType) {
    return (
      <Box pb="8" pt="10">
        <Box
          borderWidth="1px"
          borderColor="smBorder"
          bg="smBackground"
          rounded="lg"
          p="5"
          mb="8"
        >
          <Text fontWeight="semibold" fontSize="sm" color="smText">
            {t("Competition under a qualification system")}:{" "}
            {t(publicContext.procedureType)}
          </Text>
          {underQualificationScheme.schemeTitle && (
            <Text fontSize="xs" color="smMuted" mt="1">
              {t("Part of {{scheme}}.", {
                scheme: underQualificationScheme.schemeTitle,
              })}
            </Text>
          )}
          <Box mt="3">
            <QualificationSchemeInvitees eventId={eventId} />
          </Box>
        </Box>

        <MilestonePlanCard event={state} isEditable={state.status === "DRAFT"} />

        <StepNavigation
          onPrevious={previousStep}
          onNext={nextStep}
          nextLabel={nextStepLabel}
        />
      </Box>
    );
  }

  // FOA § 26-7 settled the procedure at the announcement — only this purchase's terms are left.
  if (isDpsCompetition(publicContext?.procedureType)) {
    return (
      <Box pb="8" pt="10">
        <Box
          borderWidth="1px"
          borderColor="smBorder"
          bg="smBackground"
          rounded="lg"
          p="5"
          mb="8"
        >
          <Text fontWeight="semibold" fontSize="sm" color="smText">
            {t("Competition under a dynamic purchasing system")}
          </Text>
          <Text fontSize="xs" color="smMuted" mt="1">
            {publicContext?.dpsCompetition?.schemeTitle
              ? t(
                  "Part of {{scheme}}. Every supplier qualified when you open it is invited.",
                  {
                    scheme: publicContext.dpsCompetition.schemeTitle,
                  },
                )
              : t("Every supplier qualified when you open it is invited.")}
          </Text>
        </Box>

        <MilestonePlanCard event={state} isEditable={state.status === "DRAFT"} />

        <StepNavigation
          onPrevious={previousStep}
          onNext={nextStep}
          nextLabel={nextStepLabel}
        />
      </Box>
    );
  }

  const choose = (value: ProcedureType) => updateField({ publicContext: { procedureType: value } });
  const legalBasisOf = (value: ProcedureType) => procedureLegalBasis(value, procedureInputs, t);
  const procedureEntries: ProcedureEntry[] = [
    ...procedureOptions.map((option) => ({
      key: option.value,
      shelf: "common" as const,
      name: t(option.value),
      description: t(option.descriptionKey),
      legalBasis: legalBasisOf(option.value),
      isSelected: selectedProcedure === option.value,
      availability: optionBarredByTier(option)
        ? { barredBecause: twoStageBarredLabel }
        : optionComingSoon(option)
          ? ("comingSoon" as const)
          : ("choosable" as const),
      onSelect: () => choose(option.value),
    })),
    ...(underUtilities
      ? [
          {
            key: "qualification-scheme",
            shelf: "repeated" as const,
            name: t("Qualification system"),
            description: t("A standing supplier list — competitions run among those admitted"),
            legalBasis: legalBasisOf(ProcedureType.QualificationScheme),
            isSelected: procedureType === ProcedureType.QualificationScheme,
            availability: overEeaThreshold ? ("choosable" as const) : { barredBecause: eeaTierLabel },
            onSelect: () => choose(ProcedureType.QualificationScheme),
          },
        ]
      : []),
    {
      key: "dynamic-purchasing-system",
      shelf: "repeated" as const,
      name: t("Dynamic purchasing system (DPS)"),
      description: t("Continuous qualification of suppliers"),
      legalBasis: underUtilities ? legalBasisOf(ProcedureType.DynamicPurchasingSystem) : null,
      isSelected: procedureType === ProcedureType.DynamicPurchasingSystem,
      availability: offersTwoStage ? ("choosable" as const) : { barredBecause: dpsBarredLabel },
      onSelect: () => choose(ProcedureType.DynamicPurchasingSystem),
    },
    ...(showsWithoutNotice
      ? [
          {
            key: "negotiation-without-notice",
            shelf: "exceptions" as const,
            name: t(ProcedureType.NegotiatedWithoutPriorCall),
            description: t(
              "Negotiate with suppliers you invite, without announcing the competition, when the regulation allows it",
            ),
            legalBasis: legalBasisOf(ProcedureType.NegotiatedWithoutPriorCall),
            isSelected: withoutNotice,
            availability: "choosable" as const,
            onSelect: () => choose(ProcedureType.NegotiatedWithoutPriorCall),
          },
        ]
      : []),
    ...(showsDesignContest
      ? [
          {
            key: "design-contest",
            shelf: "exceptions" as const,
            name: t("Design contest"),
            description: t("A jury chooses the best plan or design among anonymous entries"),
            legalBasis: legalBasisOf(ProcedureType.DesignContestOpen),
            isSelected: designContest,
            availability: contestBarred ? { barredBecause: contestBarredLabel } : ("choosable" as const),
            onSelect: () => !designContest && choose(ProcedureType.DesignContestOpen),
          },
        ]
      : []),
    ...(showsWithoutCompetition
      ? [
          {
            key: "without-competition",
            shelf: "exceptions" as const,
            name: t(ProcedureType.WithoutCompetition),
            description: t("Award directly to one supplier when the regulation allows no competition"),
            legalBasis: legalBasisOf(ProcedureType.WithoutCompetition),
            isSelected: withoutCompetition,
            availability: "choosable" as const,
            onSelect: () => choose(ProcedureType.WithoutCompetition),
          },
        ]
      : []),
  ];

  const announcedPartI = isAnnouncedPartI(publicContext);
  const noticeWarnsOfSavedProcedure = isUnavailableInItsPart({
    procedureType: publicContext?.procedureType,
    regulation,
    foaPart: publicContext?.foaPart,
    contractNature: publicContext?.contractNature,
    serviceCategory: publicContext?.serviceCategory,
    announcedOnDoffin: publicContext?.announcedOnDoffin,
  });

  return (
    <Box pb="8" pt="10">
      {announcedPartI && (
        <>
          <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="lg" p="5" mb="8">
            <Text fontWeight="semibold" fontSize="sm" color="smText">
              {t("Announced competition, open to everyone")}
            </Text>
            <Text fontSize="xs" color="smMuted" mt="1">
              {t("Part I names no procedures. Everyone who sees the notice on Doffin can submit a tender.")}
            </Text>
          </Box>
          <PlaceOfPerformanceField eventId={eventId} />
        </>
      )}
      <FormControl mb="8" display={announcedPartI ? "none" : undefined}>
        <FormLabel>
          {t("Procedure type")}{" "}
          {isSaving("publicContext.procedureType") && <Spinner size="xs" />}
        </FormLabel>
        <ProcedureUnavailableNotice
          eventId={eventId}
          procedureType={publicContext?.procedureType}
          regulation={regulation}
          foaPart={publicContext?.foaPart}
          contractNature={publicContext?.contractNature}
          serviceCategory={publicContext?.serviceCategory}
          announcedOnDoffin={publicContext?.announcedOnDoffin}
          onProcedureStep
        />
        <ProcedureShelves
          entries={procedureEntries}
          savedProcedureAlreadyWarned={noticeWarnsOfSavedProcedure}
          savedProcedureRefused={
            savedProcedureRefused ? { name: t(refusedProcedure) } : null
          }
        />
      </FormControl>

      {!savedProcedureRefused && (
        <>
          {withoutCompetition && (
            <Box mb="8">
              <NoCompetitionJustificationField
                eventId={eventId}
                justification={publicContext?.noCompetitionJustification ?? null}
                regulation={regulation}
                contestBelowEeaTier={followsDesignContestBelowEeaTier(
                  publicContext,
                )}
                isEditable={state.status === "DRAFT"}
              />
            </Box>
          )}

          {designContest && publicContext?.procedureType && (
            <Box mb="8">
              <DesignContestField
                eventId={eventId}
                contest={publicContext.designContest ?? null}
                procedureType={publicContext.procedureType}
                regulation={regulation}
                noticeRoute={state.contractNoticeRoute ?? null}
                homeDepartmentId={state.homeDepartment?.id ?? null}
                isEditable={state.status === "DRAFT"}
              />
            </Box>
          )}

          {withoutNotice && (
            <Box mb="8">
              <NegotiationWithoutNoticeField
                eventId={eventId}
                negotiation={publicContext?.negotiationWithoutNotice ?? null}
                regulation={regulation}
                contestBelowEeaTier={followsDesignContestBelowEeaTier(
                  publicContext,
                )}
                isEditable={state.status === "DRAFT"}
              />
            </Box>
          )}

          {isDpsScheme(publicContext?.procedureType) && (
            <DpsSchemeFields
              eventId={eventId}
              usage={publicContext?.dynamicPurchasingSystem ?? null}
              scheme={publicContext?.dpsScheme ?? null}
              isEditable={state.status === "DRAFT"}
            />
          )}

          {publicContext && priorInformationCallOffered(publicContext) && (
            <PriorInformationCallField
              eventId={eventId}
              call={publicContext.priorInformationCall ?? null}
              regulation={publicContext.regulation}
              urgent={usesAcceleratedProcedure(publicContext)}
              isEditable={state.status === "DRAFT"}
            />
          )}

          {isQualificationScheme(publicContext?.procedureType) && (
            <QualificationSchemeFields
              eventId={eventId}
              scheme={publicContext?.qualificationScheme ?? null}
              isEditable={state.status === "DRAFT"}
            />
          )}

          {/* The stage-1 deadline (frist for forespørsel om å delta) is set and confirmed directly
              on its node in the milestone plan, like the bid deadline for single-stage. */}
          {publicContext &&
            !withoutCompetition &&
            offersAcceleratedProcedure(publicContext.regulation, publicContext.foaPart, publicContext.procedureType) && (
              <AcceleratedProcedureField
                eventId={eventId}
                publicContext={publicContext}
                isEditable={state.status === "DRAFT"}
              />
            )}

          {!withoutCompetition && <MilestonePlanCard event={state} isEditable={state.status === "DRAFT"} />}

          {/* Nedvalg follows the plan: first when the request phase ends, then how many proceed.
              § 26-4 forbids capping a DPS — every qualified supplier is admitted. */}
          {isTwoStageProcedure(publicContext?.procedureType) &&
            !isDpsScheme(publicContext?.procedureType) &&
            !isQualificationScheme(publicContext?.procedureType) && (
              <Box mb="8">
                <CandidateLimitsField
                  eventId={eventId}
                  current={publicContext?.candidateLimits ?? null}
                  statutoryMin={minCandidatesWhenCapped(
                    publicContext?.regulation,
                    publicContext?.foaPart,
                    publicContext?.procedureType,
                  )}
                  underFoaPartIi={isFoaNationalTier(
                    publicContext?.regulation,
                    publicContext?.foaPart,
                  )}
                />
              </Box>
            )}

          {allowsNegotiation(publicContext?.procedureType) &&
            !withoutNotice &&
            (requiresProcedureJustification(
              regulation,
              partIHealthSocialEducation,
            ) ||
              offersAwardOnInitialTenders(publicContext?.procedureType)) && (
              <Box mb="8">
                <NegotiationTermsField
                  eventId={eventId}
                  justification={publicContext?.procedureJustification ?? null}
                  noNegotiationNecessary={
                    publicContext?.noNegotiationNecessary ?? null
                  }
                  requiresJustification={requiresProcedureJustification(
                    regulation,
                    partIHealthSocialEducation,
                  )}
                  procedureLeftToTheBuyer={
                    partIHealthSocialEducation ||
                    isLeftToTheBuyer(regulation, publicContext?.foaPart, publicContext?.procedureType)
                  }
                  offersAwardOnInitialTenders={offersAwardOnInitialTenders(
                    publicContext?.procedureType,
                  )}
                />
              </Box>
            )}

          {!withoutNotice && offersStagedReduction(publicContext?.procedureType) && (
            <Box mb="8">
              <StagedReductionField
                eventId={eventId}
                procedureType={publicContext?.procedureType}
                regulation={publicContext?.regulation}
                foaPart={publicContext?.foaPart}
                reducesInSuccessiveStages={publicContext?.reducesInSuccessiveStages ?? null}
                isEditable={state.status === "DRAFT"}
              />
            </Box>
          )}
        </>
      )}

      {isFoaPartII(publicContext?.regulation, publicContext?.foaPart) && (
        <Box mb="8">
          <DialoguePlanField
            eventId={eventId}
            dialoguePlanned={publicContext?.dialoguePlanned ?? null}
            dialogueDescription={publicContext?.dialogueDescription ?? null}
          />
        </Box>
      )}

      {!withoutCompetition && (
        <Box mb="8">
          <SubmissionLanguagesField eventId={eventId} />
        </Box>
      )}

      <StepNavigation
        onPrevious={previousStep}
        onNext={nextStep}
        nextLabel={nextStepLabel}
      />
    </Box>
  );
};
